import React, { useState, useRef, useEffect } from 'react';
import TimeInput from './TimeInput';
import { minutesToEst, minutesToEstDigits, estInputToMinutes } from './timeUtils';
import { shortcutOf } from './keyboard';

/**
 * TimeInput の parseTime と同じ変換。
 * 入力中の文字（確定前）から時刻を作るために使う。
 *   "9"    → 09:00
 *   "930"  → 09:30
 *   "1430" → 14:30
 *   ""     → null
 */
const parseTimeInput = (raw) => {
  const digits = String(raw == null ? '' : raw).replace(/[^0-9]/g, '');
  if (digits === '') return null;

  let h;
  let m;
  if (digits.length <= 2) {
    h = parseInt(digits, 10);
    m = 0;
  } else {
    h = parseInt(digits.slice(0, digits.length - 2), 10);
    m = parseInt(digits.slice(-2), 10);
  }

  if (isNaN(h) || isNaN(m)) return null;
  h = Math.min(23, Math.max(0, h));
  m = Math.min(59, Math.max(0, m));
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
};

function QuickAddBar({ onStart, onAdd, nameRef, actionsRef, onOpenNote }) {
  const [name, setName] = useState('');
  const [sched, setSched] = useState(null);
  const [note, setNote] = useState('');

  /**
   * 見積は「分の数値」で持ち、表示だけ HH:MM にする。
   *   estMin     … 確定した分。未入力は null
   *   estText    … 入力中の数字（4桁まで）
   *   estFocused … 入力中かどうか
   */
  const [estMin, setEstMin] = useState(null);
  const [estText, setEstText] = useState('');
  const [estFocused, setEstFocused] = useState(false);

  /**
   * 開始時刻の欄を作り直すための番号。reset のたびに増やす。
   * sched が元から null のときは setSched(null) では中身が消えないため。
   */
  const [timeKey, setTimeKey] = useState(0);

  // 短時間での重複送信を防ぐフラグ
  const isProcessing = useRef(false);

  // この行全体。開始時刻の欄を探すのに使う
  const barRef = useRef(null);

  // 外から「タスク名の欄へ移動」を呼べるようにする
  const innerRef = useRef(null);
  useEffect(() => {
    if (nameRef) nameRef.current = innerRef.current;
  }, [nameRef]);

  const reset = () => {
    setName('');
    setSched(null);
    setNote('');
    setEstMin(null);
    setEstText('');
    setEstFocused(false);
    // 開始時刻の欄を作り直して、残った数字を確実に消す
    setTimeKey((n) => n + 1);
    isProcessing.current = false;
  };

  /** 入力中ならその値を、そうでなければ確定値を使う */
  const currentEst = () => {
    if (!estFocused) return estMin;
    const parsed = estInputToMinutes(estText);
    return parsed === undefined ? estMin : parsed;
  };

  /**
   * 開始時刻の「いま見えている値」を取る。
   * 見積時間と同じ考え方で、確定していなくても入力中の文字から作る。
   * 欄が見つからないときだけ、確定済みの値を使う。
   */
  const currentSched = () => {
    const el = barRef.current && barRef.current.querySelector('.qa-time');
    if (!el) return sched;
    return parseTimeInput(el.value);
  };

  const handleEstFocus = (e) => {
    const el = e.target;
    setEstFocused(true);
    setEstText(minutesToEstDigits(estMin));
    requestAnimationFrame(() => el.select());
  };

  const handleEstBlur = () => {
    setEstFocused(false);
    const parsed = estInputToMinutes(estText);
    // 無効なときは元の値に戻す
    if (parsed !== undefined) setEstMin(parsed);
  };

  const values = () => [name.trim(), currentEst(), currentSched(), note];

  const handleStart = () => {
    // 処理中なら無視
    if (isProcessing.current) return;
    isProcessing.current = true;
    onStart(...values());
    reset();
  };

  const handleAdd = () => {
    // 処理中なら無視（短時間での重複送信を防ぐ）
    if (isProcessing.current) return;
    isProcessing.current = true;
    onAdd(...values());
    reset();
  };

  /**
   * どの欄でも同じキーで動くようにする。
   * 入力欄より先に受け取る（Capture）ので、途中で止められることがない。
   *   Ctrl/Cmd + J       … タスク追加欄へフォーカス移動
   *   Ctrl/Cmd + K       … 実行中タスク欄へフォーカス移動（TaskManager で処理）
   *   Ctrl/Cmd + Enter   … タスク追加
   *   Shift + Enter      … すぐ開始
   *   Esc                … 入力中の内容を消す
   * Enter 単体では何もしない（変換の確定で誤って追加されるのを防ぐ）
   *
   * ここで処理しきるものだけ stopPropagation する。
   * focusRunning と toggle は親に渡す必要があるので止めない。
   */
  const handleKey = (e) => {
    // 処理中なら全てのキーボード入力を無視
    if (isProcessing.current) return;

    if (e.key === 'Escape') {
      e.preventDefault();
      e.stopPropagation();
      reset();
      return;
    }

    const action = shortcutOf(e);

    if (action === 'focusAdd') {
      // Ctrl/Cmd + J → タスク名の欄へ移動。ここで完結する
      e.preventDefault();
      e.stopPropagation();
      const el = innerRef.current;
      if (el) {
        el.focus();
        el.select();
      }
    } else if (action === 'add') {
      // Ctrl/Cmd + Enter → タスク追加。ここで完結する
      e.preventDefault();
      e.stopPropagation();
      handleAdd();
    } else if (action === 'start') {
      // Shift + Enter → すぐ開始。ここで完結する
      e.preventDefault();
      e.stopPropagation();
      handleStart();
    }
    // focusRunning（Ctrl/Cmd + K）と toggle は親で処理するので、そのまま通す
  };

  // 画面のどこからでも呼べるようにしておく（Shift + Enter など）
  if (actionsRef) actionsRef.current = { start: handleStart, add: handleAdd };

  // メモの中身を、別画面から読み書きできるようにしておく
  if (actionsRef) {
    actionsRef.current.getNote = () => note;
    actionsRef.current.setNote = setNote;
  }

  const estValue = estFocused
    ? estText
    : estMin === null || estMin === undefined
    ? ''
    : minutesToEst(estMin);

  return (
    <div className="quick-add-bar" ref={barRef} onKeyDownCapture={handleKey}>
      <button className="qa-start" onClick={handleStart} title="追加してすぐ開始">
        開始
      </button>
      <button className="qa-plus" onClick={handleAdd} title="予定に追加">
        ＋
      </button>
      <input
        ref={innerRef}
        className="qa-name"
        type="text"
        value={name}
        placeholder="タスク名"
        onChange={(e) => setName(e.target.value)}
      />
      <TimeInput
        key={timeKey}
        className="qa-time"
        value={sched}
        onChange={setSched}
        title="開始予定時刻"
      />
      <input
        className="qa-est"
        type="text"
        inputMode="numeric"
        placeholder="--:--"
        maxLength={4}
        value={estValue}
        onChange={(e) => setEstText(e.target.value.replace(/[^0-9]/g, ''))}
        onFocus={handleEstFocus}
        onBlur={handleEstBlur}
        title="見積時間。1〜3桁は分、4桁は時分（80→01:20 / 0120→01:20）"
      />
      <input
        className="row-note"
        type="text"
        value={note}
        placeholder="メモ"
        title={note}
        onChange={(e) => setNote(e.target.value)}
      />
      <button
        type="button"
        className={`btn-note ${note ? 'has-note' : ''}`}
        onClick={() => onOpenNote && onOpenNote()}
        title="メモ"
      >
        ✎
      </button>
    </div>
  );
}

export default QuickAddBar;
